import { useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { DataTableColumn } from 'common/components/dataTable/models';
import { AlertsListItemDto } from 'api/alerts/models/AlertsListItemDto';
import { AlertType } from 'api/alerts/enums/AlertType';
import { getAlertTypeColor } from 'common/utils/AlertsUtils';
import { DATE_FORMAT_DEFAUT, DATE_TIME_FORMAT_DEFAULT, DefaultAssetImage } from 'Config';
import AvatarWithText from 'common/components/avatar/AvatarWithText';
import Badge from 'common/components/badge/Badge';
import CheckBox from 'common/components/checkBox/CheckBox';
import DateFormat from 'common/components/dateFormat/dateFormat';
import styles from '../AlertsScreen.module.scss';
import React from 'react';

type UseAlertsTableColumnsOptions = {
    hideVehicleColumns?: boolean;
    hasAlertsWritePolicy: boolean;
    editable: boolean;
};

const useAlertsTableColumns = ({ hideVehicleColumns, hasAlertsWritePolicy, editable }: UseAlertsTableColumnsOptions): DataTableColumn<AlertsListItemDto>[] => {
    const { t } = useTranslation();
    const imageCacheKey = useRef(new Date().getTime());

    return useMemo(() => {
        const allColumns: DataTableColumn<AlertsListItemDto>[] = [
            {
                field: 'vehiclePhotoUrl' as keyof AlertsListItemDto,
                name: t('common.image') as string,
                width: 90,
                hideOn: ['sm'],
                hideOrderButton: true,
                excludeFromExport: true,
                renderCell: row => (row.type !== AlertType.EXPENSE_FILE_INVALID_NEW_ALERT && row.type !== AlertType.EXPENSE_FILE_PROCESSED_NEW_ALERT)
                    ? <div className={styles.imageDescCell}>
                        <div className={styles.contentImage} style={{ width: '5rem', height: '3rem' }}>
                            <div className={styles.img} style={{ width: '5rem', height: '3rem', backgroundImage: row?.vehiclePhotoUrl ? 'url(' + row?.vehiclePhotoUrl + '?_=' + imageCacheKey.current + ')' : row?.vehicleSegmentDefaultPhotoUrl ? 'url(' + row?.vehicleSegmentDefaultPhotoUrl + '?_=' + imageCacheKey.current + ')' : 'url(' + DefaultAssetImage + ')' }} />
                        </div>
                    </div>
                    : <></>,
            },
            {
                field: 'vehiclePlate' as keyof AlertsListItemDto,
                name: t('vehicle.registration') as string,
                width: 120,
                orderColumn: 'v.registration_number',
                renderCell: row => (row.type !== AlertType.EXPENSE_FILE_INVALID_NEW_ALERT && row.type !== AlertType.EXPENSE_FILE_PROCESSED_NEW_ALERT)
                    ? <div className={styles.columnPlate}>{row.vehiclePlate}</div>
                    : <></>,
            },
            {
                field: 'vehicleBrandName' as keyof AlertsListItemDto,
                name: t('vehicle.brand') as string,
                width: 130,
                hideOn: ['sm', 'md'],
                orderColumn: 'vb.name',
                renderCell: row => <>{row.vehicleBrandName || '-'}</>,
            },
            {
                field: 'vehicleModelName' as keyof AlertsListItemDto,
                name: t('vehicle.model') as string,
                width: 130,
                hideOn: ['sm', 'md'],
                orderColumn: 'vm.name',
                renderCell: row => <>{row.vehicleModelName || '-'}</>,
            },
            {
                field: 'userName' as keyof AlertsListItemDto,
                name: t('alerts.list.alert_receiver') as string,
                width: 180,
                hideOn: ['sm', 'md', 'lg'],
                orderColumn: 'anu.real_name',
                renderCell: row => <AvatarWithText src={row.userPhotoUrl} size="x2" hideAvatar={!row.userName}>
                    <div className={styles.bold}>{row.userName || '-'}</div>
                </AvatarWithText>,
                exportValue: row => row.userName ?? '',
            },
            {
                field: 'createdDate' as keyof AlertsListItemDto,
                name: t('alerts.list.date') as string,
                width: 140,
                orderColumn: 'a.created_date',
                exportValue: row => row.createdDate ? moment(row.createdDate).format(DATE_TIME_FORMAT_DEFAULT) : '',
                renderCell: row => <DateFormat value={row.createdDate} format={DATE_TIME_FORMAT_DEFAULT} />,
            },
            {
                field: 'type' as keyof AlertsListItemDto,
                name: t('alerts.list.type') as string,
                width: 150,
                hideOn: ['sm'],
                orderColumn: 'a.type',
                renderCell: row => {
                    let expenseStatusIsAproved = null;
                    if (row.type === AlertType.EXPENSE_TREATED_ALERT || row.type === AlertType.EXPENSE_NOT_TREATED_ALERT || row.type === AlertType.EXPENSE_REFUND_APPROVED_ALERT || row.type === AlertType.EXPENSE_REFUND_REFUSED_ALERT) {
                        expenseStatusIsAproved = 'EXPENSE_SITUATION_ALERT';
                    }
                    return <>
                        <Badge
                            backgroundColor={getAlertTypeColor(row.type).rowColor}
                            textColor={row.color?.textColor}
                        >
                            {row.subject ? expenseStatusIsAproved == null ? t(`alerts.types.${row.type}` as any) : t(`alerts.types.${expenseStatusIsAproved}` as any) : ''}
                        </Badge>
                    </>;
                },
                exportValue: row => {
                    if (!row.subject) return '';
                    if (row.type === AlertType.EXPENSE_TREATED_ALERT || row.type === AlertType.EXPENSE_NOT_TREATED_ALERT || row.type === AlertType.EXPENSE_REFUND_APPROVED_ALERT || row.type === AlertType.EXPENSE_REFUND_REFUSED_ALERT) {
                        return t('alerts.types.EXPENSE_SITUATION_ALERT' as any) as string;
                    }
                    return t(`alerts.types.${row.type}` as any) as string;
                },
            },
            {
                field: 'subject' as keyof AlertsListItemDto,
                name: t('common.description') as string,
                width: 200,
                hideOn: ['sm', 'md'],
                orderColumn: 'a.subject',
                renderCell: row => <div className={styles.typeDescription}>{row.subject}</div>,
            },
            {
                field: 'message' as keyof AlertsListItemDto,
                name: t('alerts.list.message') as string,
                width: 320,
                hideOn: ['sm'],
                orderColumn: 'a.message',
                renderCell: (row, _col, _idx) => (
                    <div className={styles.messageContainer} onClick={e => { e.stopPropagation() }}>
                        <p className={styles.message} dangerouslySetInnerHTML={{ __html: row.message }} />
                    </div>
                ),
            },
            {
                field: 'readDate' as keyof AlertsListItemDto,
                name: t('alerts.list.read') as string,
                width: 140,
                orderColumn: 'au.read_date is null',
                renderCell: row => <>
                    {hasAlertsWritePolicy && editable
                        ? <CheckBox
                            checked={Boolean(row.readDate)}
                            readOnly
                            round
                            color={Boolean(row.readDate) ? row.colorType?.backgroundColor : undefined}
                        />
                        : <></>}
                    {row.readDate && <div className={styles.dateReaded}>{t('alerts.list.read')}:&nbsp;<DateFormat value={row.readDate} format={DATE_FORMAT_DEFAUT} /></div>}
                </>,
                exportValue: row => row.readDate ? moment(row.readDate).format(DATE_FORMAT_DEFAUT) : '',
            },
        ];

        if (hideVehicleColumns) {
            const vehicleFields: string[] = ['vehiclePhotoUrl', 'vehiclePlate', 'vehicleBrandName', 'vehicleModelName'];
            return allColumns.filter(col => !vehicleFields.includes(col.field as string));
        }

        return allColumns;
    }, [t, hasAlertsWritePolicy, editable, hideVehicleColumns]);
};

export default useAlertsTableColumns;
